import { ENDPOINT_TRANSCRIPT } from "@constants/urls";
import { getHubAuthHeaders } from "@helpers/lib/hub-auth";
import { captureErrorToSentry } from "@helpers/sentry-logger";

export type TranscriptionOptions = {
	language?: string;
	responseFormat?: "json" | "text" | "srt" | "verbose_json" | "vtt";
	temperature?: number;
	prompt?: string;
};

export type TranscriptionResult = {
	text: string;
	success: boolean;
	error?: string;
};

/**
 * Transcribes audio using OpenAI's Whisper API
 * @param audioBlob - The audio blob to transcribe
 * @param options - Optional transcription parameters
 * @returns Promise<TranscriptionResult>
 */
export const transcribeAudio = async (
	audioBlob: Blob,
	options: TranscriptionOptions = {},
): Promise<TranscriptionResult> => {
	try {
		const formData = new FormData();

		// Determine file extension based on blob type
		const mimeType = audioBlob.type || "audio/webm";
		const extension =
			mimeType.includes("mp4") || mimeType.includes("m4a") ? "m4a" : "webm";

		formData.append("file", audioBlob, `audio.${extension}`);
		formData.append("model", "whisper-1");
		formData.append("response_format", options.responseFormat || "json");

		if (options.language) {
			formData.append("language", options.language);
		}

		if (options.temperature !== undefined) {
			formData.append("temperature", options.temperature.toString());
		}

		if (options.prompt) {
			formData.append("prompt", options.prompt);
		}

		const response = await fetch(ENDPOINT_TRANSCRIPT, {
			method: "POST",
			// Auth headers only - never set Content-Type for FormData (the
			// browser must set the multipart boundary itself).
			headers: await getHubAuthHeaders(),
			body: formData,
		});

		if (!response.ok) {
			throw new Error(
				`Transcription API error: ${response.status} ${response.statusText}`,
			);
		}

		const result = await response.json();
		// The endpoint returns `{ transcript }` where transcript is the plain
		// text. Some backends instead return a JSON string ('{"text":"..."}')
		// or `{ text }`, so handle all three rather than assuming JSON.
		const raw = result.transcript ?? result.text ?? "";
		let transcribedText = "";
		if (typeof raw === "string") {
			try {
				const parsed = JSON.parse(raw);
				transcribedText =
					parsed && typeof parsed.text === "string" ? parsed.text : raw;
			} catch {
				transcribedText = raw;
			}
		}

		return {
			text: transcribedText.trim(),
			success: true,
		};
	} catch (error) {
		const errorMessage =
			error instanceof Error ? error.message : "Unknown transcription error";

		captureErrorToSentry(new Error("Audio transcription failed"), {
			error: errorMessage,
			blobSize: audioBlob.size,
			blobType: audioBlob.type,
		});

		return {
			text: "",
			success: false,
			error: errorMessage,
		};
	}
};

/**
 * Transcribes audio from multiple blob chunks
 * @param chunks - Array of audio blob chunks
 * @param options - Optional transcription parameters
 * @returns Promise<TranscriptionResult>
 */
export const transcribeAudioChunks = async (
	chunks: Blob[],
	options: TranscriptionOptions = {},
): Promise<TranscriptionResult> => {
	if (chunks.length === 0) {
		return {
			text: "",
			success: false,
			error: "No audio chunks provided",
		};
	}

	// Combine chunks into a single blob
	const audioBlob = new Blob(chunks, { type: "audio/webm" });
	return await transcribeAudio(audioBlob, options);
};
